import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VocabPair, VocabPairContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** German↔English vocab matching. Each German term picks its English translation
 *  from a shuffled list. Graded when every pair maps to its own translation. */
export function VocabPairRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as VocabPairContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  const pairs: VocabPair[] = c.pairs ?? [];
  const map = (answer as Record<string, string>) ?? {};

  const choices = useMemo(() => {
    const en = pairs.map((p) => p.en);
    for (let i = en.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [en[i], en[j]] = [en[j], en[i]];
    }
    return en;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFor = (pairId: string, en: string) => setAnswer({ ...map, [pairId]: en });

  return (
    <div>
      <Prompt>{c.prompt || t("Match each German word to its English meaning")}</Prompt>
      <div className="mt-4 space-y-3">
        {pairs.map((p) => {
          const chosen = map[p.pair_id] ?? "";
          const ok = revealed ? chosen === p.en : null;
          return (
            <div key={p.pair_id}>
              <div className="mb-1 flex items-baseline gap-2">
                <span className="text-sm font-semibold text-ink">
                  {enableAnswerGloss ? <GlossableText text={p.de} context={c.prompt} /> : p.de}
                </span>
                {p.hint && (
                  <span className="text-xs text-ink-faint">
                    ({enableAnswerGloss ? <GlossableText text={p.hint} context={c.prompt} /> : p.hint})
                  </span>
                )}
              </div>
              <select
                value={chosen}
                disabled={revealed}
                onChange={(e) => setFor(p.pair_id, e.target.value)}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                  revealed
                    ? ok
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-red-400 bg-red-50"
                    : "border-black/10 bg-white"
                }`}
              >
                <option value="">{t("Choose…")}</option>
                {choices.map((en, i) => (
                  <option key={i} value={en}>
                    {en}
                  </option>
                ))}
              </select>
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")} <span className="font-semibold">{p.en}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
